import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Popover,
  Button,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  useDisclosure,
  Icon,
  Tooltip,
  Wrap,
  Link as ChakraLink,
} from "@chakra-ui/react";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaExternalLinkAlt, FaInfo, FaInfoCircle } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../context/auth.context";
import {
  BookingLocation,
  BookingRequest,
  TimeInterval,
} from "../../data/occupancy.api";
import BookingSchedule from "./BookingSchedule";
import FullBookingScheduleModal from "./FullBookingScheduleModal";

// mapping the TimeInterval enum to the respective minutes
const timeIntervals = {
  [TimeInterval.ONE_HOUR]: 60,
  [TimeInterval.THIRTY_MINUTES]: 30,
  [TimeInterval.FIFTEEN_MINUTES]: 15,
  [TimeInterval.NONE]: 1,
};

type Props = {
  deskId?: string;
  bookingLocation: BookingLocation;
  onChange?: (bookingRequest: BookingRequest) => void;
  error?: boolean;
};

const environmentUrlRooms = () => {
  const prod = "https://www.lancaster.ac.uk/itpi/web/services/room";
  const staging = "https://www.lancaster.ac.uk/itpi/test/web/services/room";
  const localhost = "http://localhost:5173/services/room";

  if (window.location.href.includes("localhost")) {
    return `${localhost}`;
  } else if (window.location.href.includes("/itpi/test/lighthouse/")) {
    return `${staging}`;
  } else {
    return `${prod}`;
  }
};

const clampToInterval = (date: moment.Moment, interval: number) => {
  const remainder =
    date.minute() % interval === 0 ? 0 : interval - (date.minute() % interval);
  return date.clone().add(remainder, "minutes");
};

const BookingForm = ({ bookingLocation, onChange, error, deskId }: Props) => {
  const { userDetails } = useContext(AuthContext);

  const email = userDetails?.email;

  const isIlancasterStaff =
    userDetails?.["cognito:groups"]?.includes("iLancaster-staff");

  const { onOpen, isOpen, onClose } = useDisclosure();
  const desks = useMemo(
    () => bookingLocation.bookedEntitys.filter((e) => !e.disabled),
    [bookingLocation.bookedEntitys]
  );
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [selectedDeskId, setSelectedDeskId] = useState<string>(
    deskId ?? desks[0]?.id
  );
  // console.log(selectedDeskId);
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [privateBooking, setPrivateBooking] = useState(false);

  useEffect(() => {
    const dateTime = searchParams.get("datetime");
    const interval =
      bookingLocation.locationPreferences.timePreference.timeInterval;
    const startDateTime = clampToInterval(
      dateTime ? moment(dateTime) : moment(),
      timeIntervals[interval]
    );
    if (startDateTime.isSameOrBefore(moment(), "minute"))
      startDateTime.add(moment().diff(startDateTime) + 60000, "ms");
    const duration =
      bookingLocation.locationPreferences.defaultDuration.minutes;
    setStartTime(startDateTime.format("HH:mm"));
    setEndTime(startDateTime.add(duration, "minutes").format("HH:mm"));
    setStartDate(startDateTime.format("YYYY-MM-DD"));
    setEndDate(startDateTime.format("YYYY-MM-DD"));
  }, [searchParams, bookingLocation]);

  useEffect(() => {
    if (moment(startDate).isAfter(moment(endDate))) setEndDate(startDate);
  }, [startDate]);

  useEffect(() => {
    if (startDate === endDate) {
      if (
        moment(`${startDate} ${startTime}`).isAfter(
          moment(`${endDate} ${endTime}`)
        )
      )
        setEndTime(
          moment(`${startDate} ${startTime}`).add(59, "minute").format("HH:mm")
        );
    }
  }, [startTime, startDate, endDate]);

  useEffect(() => {
    if (startDate && startTime && endTime && selectedDeskId && email) {
      onChange?.({
        privateBooking,
        bookings: [{ deskId: selectedDeskId, email }],
        timeslot: {
          from: moment(
            `${startDate} ${startTime}`,
            "YYYY-MM-DD HH:mm"
          ).toISOString(),
          to: moment(`${endDate} ${endTime}`, "YYYY-MM-DD HH:mm").toISOString(),
        },
      });
    }
  }, [
    startDate,
    endDate,
    startTime,
    endTime,
    email,
    selectedDeskId,
    privateBooking,
    onChange,
  ]);

  return (
    <Box>
      {isIlancasterStaff ? (
        <Button
          as={ChakraLink}
          isExternal
          href={`${environmentUrlRooms()}/${bookingLocation.key}`}
          mb={3}
          pt={1}
        >
          Room Information
        </Button>
      ) : null}
      <FullBookingScheduleModal
        isOpen={isOpen}
        onClose={onClose}
        startDate={startDate}
        endDate={endDate}
        location={bookingLocation}
      />

      <FormControl mb={3}>
        <FormLabel>Desk</FormLabel>
        <Select
          value={selectedDeskId}
          onChange={(e) => setSelectedDeskId(e.target.value)}
        >
          {desks.map((desk) => (
            <option key={desk.id} value={desk.id}>
              {desk.description}
            </option>
          ))}
        </Select>
      </FormControl>

      <Wrap>
        <FormControl mb={3} w="auto" flex={1}>
          <FormLabel htmlFor="date">Start Date</FormLabel>
          <Input
            id="date"
            name="date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </FormControl>
        <FormControl mb={3} w="auto" flex={1}>
          <FormLabel htmlFor="startTime">Start Time</FormLabel>
          <Input
            id="startTime"
            name="startTime"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </FormControl>
      </Wrap>

      <Wrap mt={2}>
        <FormControl mb={3} w="auto" flex={1}>
          <FormLabel htmlFor="date">End Date</FormLabel>
          <Input
            id="date"
            name="date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </FormControl>
        <FormControl mt={3} w="auto" flex={1}>
          <FormLabel htmlFor="endTime">End Time</FormLabel>
          <Input
            id="endTime"
            name="endTime"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </FormControl>
      </Wrap>

      <Button w="full" my={2} onClick={() => {
        setStartTime("09:00");
        setEndTime("17:30");
      }}> Book full day</Button>

      <FormControl my={3}>
        <FormLabel display="flex" gap={2}>
          Booking schedule{" "}
          <Popover>
            <PopoverTrigger>
              <Box cursor="pointer">
                <Icon as={FaInfoCircle} w={3} h={3} opacity={0.4} />
              </Box>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>Tap/Hover to see who has the booking</PopoverBody>
            </PopoverContent>
          </Popover>
          <Tooltip label="View Full Schedule">
            <IconButton
              onClick={onOpen}
              icon={<FaExternalLinkAlt />}
              size="xs"
              aria-label="View full schedule"
              variant="ghost"
            />
          </Tooltip>
        </FormLabel>
        <BookingSchedule
          location={bookingLocation}
          deskId={selectedDeskId}
          startTime={startTime}
          endTime={endTime}
          startDate={startDate}
          endDate={endDate}
          error={error}
        />
      </FormControl>

      <FormControl mb={3} display="flex" alignItems="center" gap={2}>
        <Checkbox
          checked={privateBooking}
          onChange={(e) => setPrivateBooking(e.target.checked)}
        >
          Private booking
        </Checkbox>
        <Popover>
          <PopoverTrigger>
            <Box cursor="pointer">
              <Icon as={FaInfoCircle} w={3} h={3} opacity={0.4} />
            </Box>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>Private booking</PopoverHeader>
            <PopoverBody>
              Making your booking 'private' will hide your name in the booking
              schedule.
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </FormControl>
    </Box>
  );
};

export default BookingForm;
